/** Plain verse presentation data and pure formatting functions. */
import { makeRange, type VidRange } from "./ranges";
import {
  allHeadingsRegex,
  type BookId,
  separateHeadingsRegex,
  type StandaloneVerseData,
  wordRegex,
} from "@rsc/scripture-model";
import { createPartialVerse, type PartialVerse } from "./verseParts";
import { formatVerseRange, getTranslationSuffix } from "./util/render";
import { findRanges, ordinalsToBooleans, wrapWords } from "./util/arrays";

/**
 * Count leading heading words so displayed text retains the original verse word offsets.
 */
function countNumberOfHeadingWordsToSkip(text: string): number {
  const headingRegexResult = allHeadingsRegex.exec(text);
  if (!headingRegexResult) {
    return 0;
  }
  const matchWord = headingRegexResult[0].match(wordRegex);
  if (!matchWord) {
    return 0;
  }
  return matchWord.length;
}
/**
 * Convert scripture emphasis and line-break markers to HTML; this does not sanitize the input.
 */
function replaceFormattingWithHtml(text: string): string {
  // HTML emphasis.
  text = text.replace(/\[i]/g, "<em>").replace(/\[\/i]/g, "</em>");
  // Replace all occurrences of "[br]" with "<br>"
  return text.split("[br]").join("<br>");
}
export interface Verse {
  fromVid: number;
  toVid: number;
  tid: string;
  bid: BookId;
  tr: string;
  chapterNumber: number;
  label: string;
  text: string;
  partialVerses: PartialVerse[];
  bookName: string;
  translationName: string;
  translationAbbr: string;
}
/**
 * Assemble plain verse presentation data without fetching or validating a revision.
 * The supplied partial-verse array is retained by reference.
 */
export function makeVerse(
  fromVid: number,
  toVid: number,
  tid: string,
  bid: BookId,
  chapter: number,
  label: string,
  text: string,
  partialVerses: PartialVerse[],
  bookName: string,
  translationName: string,
  translationAbbr: string,
  tr = "",
): Verse {
  return {
    fromVid,
    toVid,
    tid,
    tr,
    bid,
    chapterNumber: chapter,
    label,
    text,
    partialVerses,
    bookName,
    translationName,
    translationAbbr,
  };
}
/**
 * Format a verse reference with an optional part label and translation suffix.
 * The plain flag renders the suffix as text instead of an HTML abbreviation.
 */
export function renderVerseReference(
  verse: Verse,
  plain: boolean = false,
  partLabel: string = "",
  showTranslation: boolean = true,
): string {
  const translationStr = getTranslationSuffix(
    showTranslation,
    verse.translationName,
    verse.translationAbbr,
    plain,
  );
  const chapterLabel = String(verse.chapterNumber);
  return formatVerseRange(
    verse.label,
    verse.label,
    verse.bookName || "",
    chapterLabel,
    chapterLabel,
    `${partLabel}${translationStr}`,
  );
}
/**
 * Extract words in source order, including heading words, to preserve stored ordinal positions.
 */
export function verseWords(verse: Verse): string[] {
  return verse.text.match(wordRegex) || [];
}
/**
 * Extract heading text from scripture heading markers in source order.
 */
export function verseHeadings(verse: Verse): string[] {
  let match;
  const result: string[] = [];
  while ((match = separateHeadingsRegex.exec(verse.text)) !== null) {
    if (match[1]) {
      result.push(match[1]);
    }
  }
  return result;
}
/**
 * Build an HTML heading from the requested book/chapter, translation, and text headings.
 * Translation information is included only when the book/chapter heading is enabled.
 */
export function makeVerseHeading(
  verse: Verse,
  includeTextHeadings: boolean,
  includeBookChapter: boolean,
  includeTranslation: boolean,
): string {
  const headings = [];
  if (includeBookChapter) {
    if (includeTranslation) {
      headings.push(
        `${verse.bookName} ${verse.chapterNumber} (${verse.translationAbbr})`,
      );
    } else {
      headings.push(`${verse.bookName} ${verse.chapterNumber}`);
    }
  }
  if (includeTextHeadings) {
    headings.push(...verseHeadings(verse));
  }
  return headings.join("<br>");
}
/**
 * Infer a chapter opening from a label beginning with verse number 1, including labels such as 1a.
 */
export function isFirstVerseInChapter(verse: Verse): boolean {
  // Assume the first verse in the chapter is "1" or "1a".
  return parseInt(verse.label) === 1;
}
/**
 * Render verse body HTML with partial-verse spans and highlighted word runs, omitting leading headings.
 * Highlight ordinals refer to the original text including headings. The verse is not mutated;
 * returned HTML still requires the caller's normal sanitization.
 */
export function formatVerseText(
  verse: Verse,
  highlightWordOrdinals: number[] = [],
): string {
  const nSkipWords = countNumberOfHeadingWordsToSkip(verse.text);
  let wordsAfterHeadings = verseWords(verse).slice(nSkipWords);
  // Wrap `span`s around all defined partial verses.
  verse.partialVerses.forEach((part) => {
    wordsAfterHeadings = wrapWords(
      wordsAfterHeadings,
      part.fromOrdinal,
      part.toOrdinal,
      `<span class='partial-verse' data-label='${part.label}'>`,
      "</span>",
      -nSkipWords,
    );
  });
  // Wrap `strong`s around all ranges of highlighted words.
  findRanges(
    ordinalsToBooleans(highlightWordOrdinals, verseWords(verse).length),
    (x) => x,
  ).forEach((highlightedRange) => {
    wordsAfterHeadings = wrapWords(
      wordsAfterHeadings,
      highlightedRange[0],
      highlightedRange[1] - 1,
      "<strong>",
      "</strong>",
      -nSkipWords,
    );
  });
  return replaceFormattingWithHtml(wordsAfterHeadings.join(" "));
}
/**
 * Render a partial verse as HTML using inclusive part bounds and full-verse highlight ordinals.
 * The verse is not mutated; formatting conversion does not sanitize the returned HTML.
 */
export function formatVersePartText(
  verse: Verse,
  part: PartialVerse,
  highlightWordOrdinals: number[] = [],
) {
  let words = verseWords(verse).slice(part.fromOrdinal, part.toOrdinal + 1);
  // Wrap `strong`s around all ranges of highlighted words.
  findRanges(
    ordinalsToBooleans(highlightWordOrdinals, verseWords(verse).length),
    (x) => x,
  ).forEach((highlightedRange) => {
    words = wrapWords(
      words,
      highlightedRange[0],
      highlightedRange[1] - 1,
      "<strong>",
      "</strong>",
      -part.fromOrdinal,
    );
  });
  return replaceFormattingWithHtml(words.join(" "));
}
/**
 * Create a range covering the verse, preserving its translation revision and selecting no words.
 */
export function verseRange(verse: Verse): VidRange {
  return makeRange(verse.fromVid, verse.toVid, verse.tid, null, verse.tr);
}
/**
 * Map an API verse DTO into plain presentation data, copying partial-verse records and retaining its revision.
 */
export function createVerse(data: StandaloneVerseData): Verse {
  return makeVerse(
    data.fromVid,
    data.toVid,
    data.tid,
    data.bid,
    data.chapterNumber,
    data.label,
    data.text,
    data.parts.map(createPartialVerse),
    data.bookName,
    data.translationName,
    data.translationAbbr,
    data.tr,
  );
}
